import React, { useMemo, useState, useEffect } from 'react';
import type { StaffMember, Shift, Schedule, AvailabilityStatus, DayOfWeek, Notification, TimeOffRequest, TimeOffRequestStatus, TimeOffRequestType, Department, ScheduleEntry, ShuttleReservation, PickupLocation, ParkingReservation, Theme, AttendanceRecord, AttendanceRequest } from '../../types';
import { DAY_INDEX_MAP } from '../../types';
import { getWeekStartDate, getWeekDates, formatDateYYYYMMDD, formatDayAndDate, formatDateRange, getDayOfWeekFromDate } from '../../utils/dateUtils';
import AvailabilityCalendar from '../staff/AvailabilityCalendar';
import Accordion from '../ui/Accordion';
import { Icons } from '../ui/Icons';
import { isEqual } from 'lodash-es';
import NotificationBell from '../notifications/NotificationBell';
import { ThemeToggle } from '../layout/Header';
import NotificationItem from '../notifications/NotificationItem';
import { toast } from 'react-hot-toast';
import ReplacementSelectionModal from './ReplacementSelectionModal';
import DateRangeSelectionModal from './DateRangeSelectionModal';
import { useDeleteHandler } from '../../hooks/useDeleteHandler';
import ConfirmationModal from '../ui/ConfirmationModal';
import DailyShiftDetailsModal from './DailyShiftDetailsModal';
import HelpCenterView from '../help/HelpCenterView';
import WorkingHoursTab from './WorkingHoursTab';
import MemosView from '../memos/MemosView';
import TaskManagerView from '../tasks/TaskManagerView';

interface StaffPortalProps {
    staffMember: StaffMember;
    shifts: Shift[];
    schedule: Schedule;
    departments: Department[];
    allStaff: StaffMember[];
    onSaveStaff: (staff: StaffMember) => void;
    onUpdateScheduleEntry?: (staffId: string, date: string, updates: { actualStartTime: string, actualEndTime: string, reason: string }) => void;
    attendanceRecords: AttendanceRecord[];
    attendanceRequests: AttendanceRequest[];
    notifications: Notification[];
    onMarkNotificationAsRead: (id: string) => void;
    onMarkAllNotificationsAsRead: () => void;
    onClearNotifications: () => void;
    timeOffRequests: TimeOffRequest[];
    onCreateTimeOffRequest: (request: Omit<TimeOffRequest, 'id' | 'userId' | 'status' | 'createdAt' | 'staffName'>) => void;
    onDeleteTimeOffRequest: (requestId: string) => void;
    onReplacementDecision: (requestId: string, accepted: boolean) => void;
    shuttleReservations: ShuttleReservation[];
    pickupLocations: PickupLocation[];
    onLogout: () => void;
    onSaveShuttleReservation: (reservation: Omit<ShuttleReservation, 'id'> & { id?: string }) => void;
    onDeleteShuttleReservation: (id: string) => void;
    onInitiateShuttlePrint?: (date: string) => void;
    propertyName: string;
    weekStartDay: DayOfWeek;
    theme: Theme;
    setTheme: (theme: Theme) => void;
}

const StaffPortal: React.FC<StaffPortalProps> = ({ 
    staffMember, 
    shifts = [], 
    schedule = {}, 
    departments = [], 
    allStaff = [], 
    onSaveStaff, 
    onUpdateScheduleEntry,
    attendanceRecords = [],
    attendanceRequests = [],
    notifications = [], 
    onMarkNotificationAsRead, 
    onMarkAllNotificationsAsRead, 
    onClearNotifications,
    timeOffRequests = [], 
    onCreateTimeOffRequest, 
    onDeleteTimeOffRequest, 
    onReplacementDecision,
    shuttleReservations = [],
    pickupLocations = [],
    onSaveShuttleReservation,
    onDeleteShuttleReservation,
    onLogout,
    onInitiateShuttlePrint,
    propertyName,
    weekStartDay,
    theme,
    setTheme
}) => {
    const [activeTab, setActiveTab] = useState<'schedule' | 'profile' | 'availability' | 'shuttle' | 'help' | 'hours' | 'memos' | 'taskManager'>('schedule');
    const [portalView, setPortalView] = useState<'main' | 'notifications'>('main');
    const [editableStaff, setEditableStaff] = useState<StaffMember>(staffMember);
    const [passwordVisible, setPasswordVisible] = useState(false);
    const [selectedDateForDetails, setSelectedDateForDetails] = useState<string | null>(null);
    const [shuttleDate, setShuttleDate] = useState(formatDateYYYYMMDD(new Date()));
    const [isBookingShuttle, setIsBookingShuttle] = useState(false);
    const [bookingForm, setBookingForm] = useState<Omit<ShuttleReservation, 'id' | 'createdAt' | 'date'>>({
        locationId: '',
        direction: 'FROM_HOTEL',
        time: '09:00',
        guests: 1,
        roomNumber: '',
        status: 'pending'
    });

    // Time Off Request Form State
    const [timeOffStartDate, setTimeOffStartDate] = useState('');
    const [timeOffEndDate, setTimeOffEndDate] = useState('');
    const [timeOffReason, setTimeOffReason] = useState('');
    const [timeOffType, setTimeOffType] = useState<TimeOffRequestType>('DIRECT');
    const [replacementStaffId, setReplacementStaffId] = useState<string | null>(null);
    const [isReplacementModalOpen, setIsReplacementModalOpen] = useState(false);
    const [isDateModalOpen, setIsDateModalOpen] = useState(false);

    const timeOffDeleteHandler = useDeleteHandler<TimeOffRequest>({
        onDeleteAsync: async (req) => onDeleteTimeOffRequest(req.id),
        entityName: 'Time Off Request',
        getDisplayName: (req) => `for ${req.startDate}`,
        getItemId: (req) => req.id,
    });


    const shuttleDeleteHandler = useDeleteHandler<ShuttleReservation>({
        onDeleteAsync: async (res) => onDeleteShuttleReservation(res.id),
        entityName: 'Shuttle Reservation',
        getDisplayName: (res) => `${(res.roomNumber || '').toString().replace(/^R/i, '')} at ${res.time}`,
        getItemId: (res) => res.id,
    });

    useEffect(() => {
        setEditableStaff(staffMember);
    }, [staffMember]);

    const personalSchedule = schedule[staffMember.id] || {};
    const sortedDepartments = useMemo(() => {
        return [...departments].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }, [departments]);
    const shiftsMap = useMemo(() => new Map<string, Shift>(shifts.map(s => [s.id, s])), [shifts]);
    const departmentsMap = useMemo(() => new Map<string, Department>(departments.map(d => [d.id, d])), [departments]);
    
    const weekStartDayIndex = useMemo(() => DAY_INDEX_MAP[weekStartDay], [weekStartDay]);
    
    const timeSlots = useMemo(() => {
        const slots = [];
        for (let hour = 0; hour < 24; hour++) {
            for (let min = 0; min < 60; min += 30) {
                const h = hour.toString().padStart(2, '0');
                const m = min.toString().padStart(2, '0');
                slots.push(`${h}:${m}`);
            }
        }
        return slots;
    }, []);

    const thisWeekStartDate = useMemo(() => getWeekStartDate(new Date(), weekStartDayIndex), [weekStartDayIndex]);
    const nextWeekStartDate = useMemo(() => {
        const date = new Date(thisWeekStartDate);
        date.setDate(date.getDate() + 7);
        return date;
    }, [thisWeekStartDate]);

    const thisWeekDates = useMemo(() => getWeekDates(thisWeekStartDate), [thisWeekStartDate]);
    const nextWeekDates = useMemo(() => getWeekDates(nextWeekStartDate), [nextWeekStartDate]);
    
    const hasChanges = !isEqual(staffMember, editableStaff);
    const unreadCount = useMemo(() => (notifications || []).filter(n => !n.read).length, [notifications]);

    const todayString = useMemo(() => formatDateYYYYMMDD(new Date()), []);
    const nextShiftDateString = useMemo(() => {
        const allDates = [...thisWeekDates, ...nextWeekDates];
        const upcomingAssignedDates = allDates
            .map(date => formatDateYYYYMMDD(date))
            .filter(dateString => 
                dateString >= todayString && 
                personalSchedule[dateString]?.status === 'ASSIGNED'
            )
            .sort();
        
        return (upcomingAssignedDates?.length || 0) > 0 ? upcomingAssignedDates[0] : null;
    }, [thisWeekDates, nextWeekDates, personalSchedule, todayString]);
    
    const calculateWeeklyHours = (dates: Date[], schedule: { [date: string]: ScheduleEntry }, shifts: Map<string, Shift>): number => {
        let totalHours = 0;
        for (const date of dates) {
            const dateString = formatDateYYYYMMDD(date);
            const entry = schedule[dateString];
            if (entry?.status === 'ASSIGNED' && entry.shiftId) {
                const shift = shifts.get(entry.shiftId);
                if (shift) {
                    const start = new Date(`1970-01-01T${shift.startTime}:00`);
                    const end = new Date(`1970-01-01T${shift.endTime}:00`);
                    if (end < start) {
                        end.setDate(end.getDate() + 1);
                    }
                    totalHours += (end.getTime() - start.getTime()) / (1000 * 60 * 60);
                }
            }
        }
        return totalHours;
    };

    const thisWeekHours = useMemo(() => calculateWeeklyHours(thisWeekDates, personalSchedule, shiftsMap), [thisWeekDates, personalSchedule, shiftsMap]);
    const nextWeekHours = useMemo(() => calculateWeeklyHours(nextWeekDates, personalSchedule, shiftsMap), [nextWeekDates, personalSchedule, shiftsMap]);

    const getInitials = (name?: string) => {
        if (!name) return '?';
        const names = name.split(' ');
        if (names.length > 1 && names[names.length - 1]) {
            return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
        }
        return name.substring(0, 2).toUpperCase();
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value, type } = e.target as HTMLInputElement;
        setEditableStaff(prev => ({
            ...prev, 
            [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value 
        }));
    };

    const handleOverridesChange = (overrides: { [date: string]: AvailabilityStatus; }) => {
        setEditableStaff(prev => ({ ...prev, availabilityOverrides: overrides }));
    };
    
    const handleDefaultAvailabilityChange = (day: DayOfWeek, status: AvailabilityStatus) => {
        setEditableStaff(prev => ({
            ...prev,
            availability: { ...prev.availability, [day]: status }
        }));
    };

    const handleSaveChanges = () => {
        onSaveStaff(editableStaff);
    };

    const handleTimeOffSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        // --- START VALIDATION ---
        if (!timeOffStartDate || !timeOffEndDate) {
            toast.error("Please select a start and end date.");
            return;
        }
        if (new Date(timeOffEndDate) < new Date(timeOffStartDate)) {
            toast.error("End date cannot be before the start date.");
            return;
        }

        if (timeOffType === 'REPLACED') {
            if (!replacementStaffId) {
                toast.error("Please select a replacement staff member for a 'Replaced' request.");
                return;
            }
            if (replacementStaffId === staffMember.id) {
                toast.error("You cannot select yourself as a replacement.");
                return;
            }

            const replacementStaff = allStaff.find(s => s.id === replacementStaffId);
            if (!replacementStaff) {
                toast.error("Could not find the selected replacement staff member.");
                return;
            }

            const getResolvedAvailability = (person: StaffMember, date: Date): AvailabilityStatus => {
                const dateString = formatDateYYYYMMDD(date);
                if (person.availabilityOverrides && person.availabilityOverrides[dateString]) {
                    return person.availabilityOverrides[dateString];
                }
                const dayOfWeek = getDayOfWeekFromDate(date);
                return (person.availability && person.availability[dayOfWeek]) || 'Available';
            };

            const parseDateAsLocal = (dateString: string) => {
                const [year, month, day] = dateString.split('-').map(Number);
                return new Date(year, month - 1, day);
            };

            let currentDate = parseDateAsLocal(timeOffStartDate);
            const endDate = parseDateAsLocal(timeOffEndDate);

            while (currentDate <= endDate) {
                const dateString = formatDateYYYYMMDD(currentDate);

                const availability = getResolvedAvailability(replacementStaff, currentDate);
                if (availability === 'Unavailable') {
                    toast.error(`${replacementStaff.name} is unavailable on ${dateString}.`);
                    return;
                }

                const replacementSchedule = schedule[replacementStaff.id];
                const entry = replacementSchedule?.[dateString];
                if (entry && entry.status !== 'OFF' && entry.status !== 'UNASSIGNED') {
                    toast.error(`${replacementStaff.name} already has an assignment on ${dateString}.`);
                    return;
                }

                currentDate.setDate(currentDate.getDate() + 1);
            }
        }
        // --- END VALIDATION ---

        onCreateTimeOffRequest({
            employeeId: staffMember.id,
            startDate: timeOffStartDate,
            endDate: timeOffEndDate,
            reason: timeOffReason,
            type: timeOffType,
            replacementEmployeeId: timeOffType === 'REPLACED' ? replacementStaffId : null,
        });

        // Reset form state
        setTimeOffStartDate('');
        setTimeOffEndDate('');
        setTimeOffReason('');
        setTimeOffType('DIRECT');
        setReplacementStaffId(null);
        toast.success("Your time off request has been submitted.");
    };

    const renderScheduleList = (dates: Date[]) => (
        <ul className="space-y-3">
            {dates.map(date => {
                const dateString = formatDateYYYYMMDD(date);
                const { day, date: dateNum, dayLong } = formatDayAndDate(date);
                const entry = personalSchedule[dateString];
                const shift = entry?.shiftId ? shiftsMap.get(entry.shiftId) : null;
                
                // Fallback to primary department if not explicitly set in the entry
                const deptId = entry?.departmentId || (staffMember.departmentIds && staffMember.departmentIds.length > 0 ? staffMember.departmentIds[0] : null);
                const assignedDept = deptId ? departmentsMap.get(deptId) : null;
                
                const isNextShift = dateString === nextShiftDateString;

                return (
                    <li key={dateString}>
                        <button 
                            onClick={() => setSelectedDateForDetails(dateString)}
                            className={`p-4 bg-white dark:bg-slate-800 rounded-2xl shadow-lg flex flex-col transition-all duration-300 w-full text-left group border border-transparent ${
                                isNextShift ? 'ring-2 ring-indigo-500/20 border-indigo-100 dark:border-indigo-900/30' : 'hover:border-slate-200 dark:hover:border-slate-700 hover:shadow-2xl'
                            }`}
                        >
                            <div className="flex items-center gap-3 min-w-0">
                                {/* Status Dot */}
                                {(() => {
                                    if (entry?.status === 'ASSIGNED' && shift) {
                                        return (
                                            <div className="relative flex-shrink-0">
                                                <span className="block w-2.5 h-2.5 rounded-full shadow-sm" style={{ backgroundColor: shift.color }}></span>
                                                {isNextShift && <span className="absolute inset-0 rounded-full animate-ping opacity-40" style={{ backgroundColor: shift.color }}></span>}
                                            </div>
                                        );
                                    }
                                    const status = entry?.status || 'UNASSIGNED';
                                    const isUnassigned = status === 'UNASSIGNED';
                                    return <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${isUnassigned ? 'bg-slate-200 dark:bg-slate-800' : 'bg-slate-400 dark:bg-slate-600'}`}></span>;
                                })()}

                                {/* Main Info Row */}
                                <div className="flex items-center gap-2 text-sm font-bold text-slate-900 dark:text-white truncate">
                                    <span className="uppercase whitespace-nowrap">{dayLong} {dateNum}</span>
                                    <span className="text-slate-300 dark:text-slate-600">•</span>
                                    <span className="truncate">{shift?.name || (entry?.status === 'UNASSIGNED' || !entry?.status ? 'Day Off' : entry.status.replace('_', ' '))}</span>
                                    {shift && (
                                        <>
                                            <span className="text-slate-300 dark:text-slate-600">•</span>
                                            <span className="font-mono whitespace-nowrap">{shift.startTime} → {shift.endTime}</span>
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* Secondary Info */}
                            {assignedDept && (
                                <div className="ml-5.5 mt-0.5 text-[10px] font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wider">
                                    {assignedDept.name}
                                </div>
                            )}

                            {/* Action Button */}
                            {isNextShift && (
                                <div className="flex justify-end mt-2">
                                    <span className="text-[10px] font-black uppercase tracking-[0.2em] text-white bg-gradient-to-r from-indigo-600 to-violet-600 px-4 py-1.5 rounded-full shadow-lg shadow-indigo-500/30">
                                        Next
                                    </span>
                                </div>
                            )}
                        </button>
                    </li>
                );
            })}
        </ul>
    );

    const renderScheduleView = () => {
        const WeekHeader = ({ weekTitle, dates, hours }: { weekTitle: string, dates: Date[], hours: number }) => (
            <div className="flex items-center justify-between w-full gap-4">
                <div className="min-w-0">
                    <p className="font-semibold text-lg text-gray-800 dark:text-gray-200">{weekTitle}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{formatDateRange(dates[0])}</p>
                </div>
                <div className="flex-shrink-0 text-center bg-indigo-600 dark:bg-indigo-500 text-white rounded-lg p-2 w-20 shadow-inner">
                    <p className="font-bold text-2xl leading-tight">{(hours ?? 0).toFixed(1)}</p>
                    <p className="text-xs opacity-80 leading-tight">hours</p>
                </div>
            </div>
        );
        
        return (
            <div className="space-y-4">
                <Accordion title={<WeekHeader weekTitle="This Week" dates={thisWeekDates} hours={thisWeekHours} />}>
                    {renderScheduleList(thisWeekDates)}
                </Accordion>
                <Accordion title={<WeekHeader weekTitle="Next Week" dates={nextWeekDates} hours={nextWeekHours} />}>
                    {renderScheduleList(nextWeekDates)}
                </Accordion>
            </div>
        );
    };
    
    const renderProfileView = () => {
        const staffDepartments = (departments || []).filter(d => editableStaff.departmentIds?.includes(d.id));
        const departmentNames = (staffDepartments?.length || 0) > 0 ? staffDepartments.map(d => d.name).join(', ') : 'N/A';

        return (
            <div className="space-y-4">
                <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm p-4">
                    <h3 className="font-semibold text-gray-800 dark:text-gray-200 text-lg flex items-center gap-2">
                        <Icons.User className="w-5 h-5 text-gray-400" />
                        Personal & Role Information
                    </h3>
                    <div className="mt-4 space-y-4">
                        <div>
                            <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Full Name</label>
                            <div className="relative mt-1">
                                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                    <Icons.User className="h-5 w-5 text-gray-400" />
                                </div>
                                <input type="text" name="name" id="name" value={editableStaff.name} onChange={handleInputChange} className="block w-full rounded-md border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 pl-10 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm py-2" />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Role</label>
                                <p className="mt-1 text-sm text-gray-900 dark:text-gray-200 p-2 bg-gray-100 dark:bg-slate-700/50 rounded-md truncate">{editableStaff.role}</p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Departments</label>
                                <p className="mt-1 text-sm text-gray-900 dark:text-gray-200 p-2 bg-gray-100 dark:bg-slate-700/50 rounded-md truncate">{departmentNames}</p>
                            </div>
                        </div>

                        <div>
                            <label htmlFor="notes" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Notes</label>
                            <textarea 
                                name="notes" 
                                id="notes" 
                                value={editableStaff.notes || ''} 
                                onChange={handleInputChange} 
                                rows={3} 
                                className="mt-1 block w-full border-gray-300 dark:border-slate-600 dark:bg-slate-700 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            ></textarea>
                        </div>

                        <div className="flex items-center gap-2 py-2">
                            <input
                                type="checkbox"
                                id="isDriver"
                                name="isDriver"
                                checked={editableStaff.isDriver || false}
                                onChange={handleInputChange}
                                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                            />
                            <label htmlFor="isDriver" className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                                <Icons.Bus className="w-4 h-4 text-indigo-500" />
                                I am a Driver
                            </label>
                        </div>
                    </div>
                </div>

                <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm p-4">
                    <h3 className="font-semibold text-gray-800 dark:text-gray-200 text-lg flex items-center gap-2">
                        <Icons.Lock className="w-5 h-5 text-gray-400" />
                        Contact & Security
                    </h3>
                    <div className="mt-4 space-y-4">
                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Email Address</label>
                            <div className="relative mt-1">
                                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                    <Icons.Mail className="h-5 w-5 text-gray-400" />
                                </div>
                                <input type="email" name="email" id="email" value={editableStaff.email} onChange={handleInputChange} className="block w-full rounded-md border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 pl-10 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm py-2" />
                            </div>
                        </div>
                        <div>
                            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Phone Number</label>
                            <div className="relative mt-1">
                                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                    <Icons.Phone className="h-5 w-5 text-gray-400" />
                                </div>
                                <input type="tel" name="phone" id="phone" value={editableStaff.phone} onChange={handleInputChange} required className="block w-full rounded-md border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 pl-10 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm py-2" />
                            </div>
                        </div>
                        <div>
                            <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Password</label>
                            <div className="relative mt-1">
                                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                    <Icons.Lock className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    type={passwordVisible ? 'text' : 'password'}
                                    name="password"
                                    id="password"
                                    value={editableStaff.password || ''}
                                    onChange={handleInputChange}
                                    className="block w-full rounded-md border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 pl-10 pr-10 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm py-2"
                                />
                                <button type="button" onClick={() => setPasswordVisible(!passwordVisible)} className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
                                    {passwordVisible ? <Icons.EyeOff className="h-5 w-5"/> : <Icons.Eye className="h-5 w-5"/>}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                {hasChanges && (
                    <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm mt-4 p-4 text-right sticky bottom-20 z-10">
                        <button 
                            onClick={handleSaveChanges} 
                            className="inline-flex items-center gap-2 justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-slate-800 focus:ring-indigo-500 sm:text-sm"
                        >
                            <Icons.Save className="w-4 h-4" />
                            Save Changes
                        </button>
                    </div>
                )}
            </div>
        );
    };

    const renderShuttleScheduleView = () => {
        const dailyReservations = shuttleReservations.filter(r => r.date === shuttleDate);
        const sortedReservations = [...dailyReservations].sort((a, b) => a.time.localeCompare(b.time));
        
        // Group by time slot
        const timeSlotsInUse = Array.from(new Set(sortedReservations.map(r => r.time))).sort();

        const handleBookingSubmit = (e: React.FormEvent) => {
            e.preventDefault();
            if (!bookingForm.locationId || !bookingForm.roomNumber) {
                toast.error('Please fill in all required fields');
                return;
            }

            const slotReservations = dailyReservations.filter(r => r.time === bookingForm.time);
            const isBlocked = slotReservations.some(r => r.status === 'blocked' && r.spotIndex === undefined);
            
            if (isBlocked) {
                toast.error('This time slot is completely blocked.');
                return;
            }

            const blockedSpotsCount = slotReservations.filter(r => r.status === 'blocked' && r.spotIndex !== undefined).length;
            const totalGuests = slotReservations.filter(r => r.status !== 'blocked').reduce((sum, r) => sum + r.guests, 0);
            
            const availableSpots = 14 - blockedSpotsCount - totalGuests;
            if (bookingForm.guests > availableSpots) {
                toast.error(`Not enough capacity. Only ${availableSpots} spot(s) remaining for ${bookingForm.time}.`);
                return;
            }

            onSaveShuttleReservation({
                ...bookingForm,
                date: shuttleDate,
                createdAt: new Date().toISOString()
            });

            toast.success('Reservation saved');
            setIsBookingShuttle(false);
            setBookingForm(prev => ({ ...prev, roomNumber: '', guests: 1 }));
        };

        if (isBookingShuttle) {
            return (
                <div className="space-y-4">
                    <div className="bg-white dark:bg-slate-800 p-4 rounded-lg shadow-sm">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-bold text-gray-800 dark:text-gray-200">New Shuttle Reservation</h3>
                            <button onClick={() => setIsBookingShuttle(false)} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
                                <Icons.X className="w-5 h-5" />
                            </button>
                        </div>

                        <form onSubmit={handleBookingSubmit} className="space-y-4">
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">Route</label>
                                <div className="grid grid-cols-1 gap-2">
                                    <div className="flex gap-2 mb-2">
                                        <button 
                                            type="button"
                                            onClick={() => setBookingForm(prev => ({ ...prev, direction: 'FROM_HOTEL' }))}
                                            className={`flex-1 py-2 text-xs font-bold rounded-lg border transition-all ${bookingForm.direction === 'FROM_HOTEL' ? 'bg-indigo-600 border-indigo-600 text-white shadow-md' : 'bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 text-gray-600 dark:text-gray-300'}`}
                                        >
                                            Hotel → Location
                                        </button>
                                        <button 
                                            type="button"
                                            onClick={() => setBookingForm(prev => ({ ...prev, direction: 'TO_HOTEL' }))}
                                            className={`flex-1 py-2 text-xs font-bold rounded-lg border transition-all ${bookingForm.direction === 'TO_HOTEL' ? 'bg-indigo-600 border-indigo-600 text-white shadow-md' : 'bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 text-gray-600 dark:text-gray-300'}`}
                                        >
                                            Location → Hotel
                                        </button>
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                        {pickupLocations.map(loc => (
                                            <button
                                                key={loc.id}
                                                type="button"
                                                onClick={() => setBookingForm(prev => ({ ...prev, locationId: loc.id }))}
                                                className={`px-3 py-2 rounded-lg border text-[10px] font-bold transition-all text-left flex items-center gap-2 ${bookingForm.locationId === loc.id ? 'bg-indigo-50 dark:bg-indigo-900/20 border-indigo-600 text-indigo-700 dark:text-indigo-300' : 'bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 text-gray-600 dark:text-gray-300'}`}
                                            >
                                                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: loc.color }}></div>
                                                <span className="truncate">{loc.name}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">Time</label>
                                    <select
                                        value={bookingForm.time}
                                        onChange={(e) => setBookingForm(prev => ({ ...prev, time: e.target.value }))}
                                        className="w-full bg-gray-50 dark:bg-slate-700 border-gray-300 dark:border-slate-600 rounded-lg text-sm font-bold p-2"
                                    >
                                        {timeSlots.map(slot => (
                                            <option key={slot} value={slot}>{slot}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">Guests</label>
                                    <input
                                        type="number"
                                        min="1"
                                        value={bookingForm.guests}
                                        onChange={(e) => setBookingForm(prev => ({ ...prev, guests: parseInt(e.target.value) }))}
                                        className="w-full bg-gray-50 dark:bg-slate-700 border-gray-300 dark:border-slate-600 rounded-lg text-sm font-bold p-2"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">Room Number</label>
                                <input
                                    type="text"
                                    placeholder="e.g., 402"
                                    value={bookingForm.roomNumber}
                                    onChange={(e) => setBookingForm(prev => ({ ...prev, roomNumber: e.target.value.replace(/^R/i, '') }))}
                                    className="w-full bg-gray-50 dark:bg-slate-700 border-gray-300 dark:border-slate-600 rounded-lg text-sm font-bold p-2"
                                    required
                                />
                            </div>

                            <button
                                type="submit"
                                className="w-full py-3 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-500/30"
                            >
                                Confirm Reservation
                            </button>
                        </form>
                    </div>
                </div>
            );
        }

        return (
            <div className="space-y-4">
                <div className="bg-white dark:bg-slate-800 p-4 rounded-lg shadow-sm">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                        <h3 className="font-semibold text-gray-800 dark:text-gray-200 text-lg flex items-center gap-2">
                            <Icons.Bus className="w-5 h-5 text-indigo-600" />
                            Shuttle Schedule
                        </h3>
                        <div className="flex items-center gap-2">
                            <input
                                type="date"
                                value={shuttleDate}
                                onChange={(e) => setShuttleDate(e.target.value)}
                                className="bg-gray-50 dark:bg-slate-700 border-gray-300 dark:border-slate-600 rounded-md text-sm font-bold text-indigo-600 dark:text-indigo-400 py-1 px-2 focus:ring-indigo-500 focus:border-indigo-500"
                            />
                            <button
                                onClick={() => setIsBookingShuttle(true)}
                                className="p-1.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-md hover:bg-green-200 dark:hover:bg-green-800/50 transition-colors"
                                title="New Reservation"
                            >
                                <Icons.Plus className="w-4 h-4" />
                            </button>
                            <button
                                onClick={() => {
                                    if (onInitiateShuttlePrint) {
                                        onInitiateShuttlePrint(shuttleDate);
                                    } else {
                                        window.print();
                                    }
                                }}
                                className="p-1.5 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-md hover:bg-indigo-200 dark:hover:bg-indigo-800/50 transition-colors"
                                title="Print Schedule"
                            >
                                <Icons.Printer className="w-4 h-4" />
                            </button>
                        </div>
                    </div>

                    <style type="text/css" media="print">
                    {`
                        @page { size: portrait; margin: 1cm; }
                        body { -webkit-print-color-adjust: exact; color-adjust: exact; background-color: white; }
                        /* Hide everything outside of the print area */
                        nav, header, .bottom-nav { display: none !important; }
                        .print\\:hidden { display: none !important; }
                    `}
                    </style>

                    {timeSlotsInUse.length > 0 ? (
                        <div className="space-y-4">
                            {timeSlotsInUse.map(slot => {
                                const slotReservations = sortedReservations.filter(r => r.time === slot);
                                const totalGuests = slotReservations.reduce((sum, r) => sum + r.guests, 0);
                                
                                return (
                                    <div key={slot} className="border-l-4 border-indigo-500 bg-gray-50 dark:bg-slate-700/30 p-3 rounded-r-lg">
                                        <div className="flex justify-between items-center mb-2">
                                            <span className="text-lg font-black text-indigo-600 dark:text-indigo-400 font-mono">{slot}</span>
                                            <span className="px-2 py-0.5 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-full text-xs font-bold">
                                                {totalGuests} Guests
                                            </span>
                                        </div>
                                        <div className="space-y-2">
                                            {slotReservations.map(res => {
                                                const location = pickupLocations.find(l => l.id === res.locationId);
                                                const routeLabel = res.direction === 'FROM_HOTEL' 
                                                    ? `Hotel → ${location?.name || 'Unknown'}`
                                                    : `${location?.name || 'Unknown'} → Hotel`;
                                                
                                                return (
                                                    <div key={res.id} className="flex items-center justify-between text-sm py-1 border-t border-gray-200 dark:border-slate-700 first:border-t-0">
                                                        <div className="flex items-center gap-2 min-w-0">
                                                            <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: location?.color || '#ccc' }}></div>
                                                            <span className="font-medium text-gray-900 dark:text-gray-100 truncate">{routeLabel}</span>
                                                        </div>
                                                        <div className="flex items-center gap-3 flex-shrink-0 ml-2">
                                                            <span className="text-gray-600 dark:text-gray-400 font-bold">{res.guests}p</span>
                                                            <span className="bg-gray-200 dark:bg-slate-600 px-1.5 py-0.5 rounded text-[10px] font-bold text-gray-700 dark:text-gray-300">{(res.roomNumber || '').toString().replace(/^R/i, '')}</span>
                                                            {/* Hiding delete icon temporarily as requested */}
                                                            {/* <button 
                                                                onClick={() => shuttleDeleteHandler.initiateDelete(res)}
                                                                className="text-red-400 hover:text-red-600 transition-colors"
                                                            >
                                                                <Icons.Trash2 className="w-3.5 h-3.5" />
                                                            </button> */}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="text-center py-12 bg-gray-50 dark:bg-slate-700/20 rounded-lg border-2 border-dashed border-gray-200 dark:border-slate-700">
                            <img src="/icon.svg" alt="No data" className="mx-auto h-12 w-12 opacity-20 grayscale" referrerPolicy="no-referrer" />
                            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 italic">No reservations for this date.</p>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const tabs = useMemo(() => {
        return [
            { id: 'schedule', label: 'Schedule', icon: <Icons.Calendar className="w-6 h-6"/> },
            { id: 'taskManager', label: 'Tasks', icon: <Icons.ClipboardList className="w-6 h-6"/> },
            { id: 'shuttle', label: 'Shuttle', icon: <Icons.Bus className="w-6 h-6"/> },
            { id: 'memos', label: 'Memos', icon: <Icons.FileText className="w-6 h-6"/> },
            { id: 'availability', label: 'Availability', icon: <Icons.CalendarCheck className="w-6 h-6"/> },
            { id: 'hours', label: 'Hours', icon: <Icons.Clock className="w-6 h-6"/> },
        ];
    }, []);

    const renderAvailabilityView = () => {
        return (
            <div className="space-y-4">
                <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg p-6 border border-gray-100 dark:border-slate-700/50">
                    <div className="flex items-center gap-4 mb-6">
                        <div className="p-3 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-2xl">
                            <Icons.CalendarCheck className="w-6 h-6" />
                        </div>
                        <div>
                            <h3 className="text-xl font-bold text-gray-900 dark:text-white tracking-tight">Manage Availability</h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Set your working preferences</p>
                        </div>
                    </div>
                    
                    <div className="space-y-4">
                        <Accordion title="Date-Specific Overrides">
                             <AvailabilityCalendar
                                availability={editableStaff.availability}
                                availabilityOverrides={editableStaff.availabilityOverrides || {}}
                                weekStartDay={weekStartDay}
                                onOverridesChange={handleOverridesChange}
                                onAvailabilityChange={handleDefaultAvailabilityChange}
                                variant="overrides"
                                themeStyle="staff"
                                scheduleForUser={personalSchedule}
                            />
                        </Accordion>
                        <Accordion title="Default Weekly Schedule">
                            <AvailabilityCalendar
                                availability={editableStaff.availability}
                                availabilityOverrides={editableStaff.availabilityOverrides || {}}
                                weekStartDay={weekStartDay}
                                onOverridesChange={handleOverridesChange}
                                onAvailabilityChange={handleDefaultAvailabilityChange}
                                variant="defaults"
                                themeStyle="staff"
                                scheduleForUser={personalSchedule}
                            />
                        </Accordion>
                    </div>

                    {hasChanges && (
                        <div className="mt-8 pt-6 border-t border-gray-100 dark:border-slate-700/50 flex justify-end">
                            <button 
                                onClick={handleSaveChanges} 
                                className="inline-flex items-center gap-2 justify-center rounded-2xl border border-transparent shadow-lg shadow-indigo-500/20 px-6 py-3 bg-indigo-600 text-base font-bold text-white hover:bg-indigo-700 focus:outline-none focus:ring-4 focus:ring-indigo-500/20 transition-all active:scale-95"
                            >
                                <Icons.Save className="w-5 h-5" />
                                Save Availability
                            </button>
                        </div>
                    )}
                </div>
            </div>
        );
    };


    if (portalView === 'notifications') {
        return (
            <div className="max-w-md mx-auto bg-gray-50 dark:bg-slate-900 min-h-screen animate-in fade-in duration-300">
                <div className="sticky top-0 z-20 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md border-b border-gray-100 dark:border-slate-800 px-4 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <button 
                                onClick={() => setPortalView('main')} 
                                className="p-2 -ml-2 rounded-full hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
                            >
                                <Icons.ArrowLeft className="w-6 h-6 text-gray-700 dark:text-gray-200" />
                            </button>
                            <h1 className="text-xl font-bold text-gray-900 dark:text-white">Notifications</h1>
                        </div>
                        <div className="flex gap-2">
                            {unreadCount > 0 && (
                                <button 
                                    onClick={onMarkAllNotificationsAsRead} 
                                    className="text-xs font-bold text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 px-2 py-1.5 rounded-lg hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-all"
                                >
                                    Mark all read
                                </button>
                            )}
                            {(notifications?.length || 0) > 0 && (
                                <button 
                                    onClick={onClearNotifications} 
                                    className="text-xs font-bold text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 px-2 py-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-all"
                                >
                                    Clear all
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                <div className="p-4 pb-24">
                    {(notifications?.length || 0) > 0 ? (
                        <div className="space-y-6">
                            {(() => {
                                const displayedNotifications = notifications.slice(0, 50);
                                const unread = displayedNotifications.filter(n => !n.read);
                                const read = displayedNotifications.filter(n => n.read);

                                return (
                                    <>
                                        {unread.length > 0 && (
                                            <div>
                                                <h3 className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-3 px-1">New</h3>
                                                <ul className="space-y-3">
                                                    {unread.map(n => (
                                                        <NotificationItem key={n.id} notification={n} onMarkAsRead={onMarkNotificationAsRead} />
                                                    ))}
                                                </ul>
                                            </div>
                                        )}

                                        {read.length > 0 && (
                                            <div>
                                                <h3 className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-3 px-1">Earlier</h3>
                                                <ul className="space-y-3">
                                                    {read.map(n => (
                                                        <NotificationItem key={n.id} notification={n} onMarkAsRead={onMarkNotificationAsRead} />
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                        
                                        {notifications.length > 50 && (
                                            <div className="text-center py-4">
                                                <p className="text-xs text-gray-500 italic">Showing latest 50 of {notifications.length} notifications</p>
                                            </div>
                                        )}
                                    </>
                                );
                            })()}
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center py-20 px-6 text-center">
                            <div className="w-20 h-20 rounded-full bg-gray-100 dark:bg-slate-800 flex items-center justify-center mb-6">
                                <Icons.Bell className="w-10 h-10 text-gray-300 dark:text-slate-600" />
                            </div>
                            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">All caught up!</h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                                You have no new notifications at the moment. We'll let you know when something important happens.
                            </p>
                            <button 
                                onClick={() => setPortalView('main')}
                                className="mt-8 px-6 py-2 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-gray-700 dark:text-gray-200 font-bold rounded-xl hover:bg-gray-50 dark:hover:bg-slate-700 transition-all shadow-sm"
                            >
                                Back to Dashboard
                            </button>
                        </div>
                    )}
                </div>
            </div>
        );
    }


    return (
        <div className="max-w-md mx-auto bg-gray-100 dark:bg-slate-900 min-h-screen">
            <div className="p-4 space-y-4 pb-24">
                <div className="flex items-center gap-3">
                    <button 
                        onClick={() => setActiveTab('profile')}
                        className="w-12 h-12 rounded-full flex-shrink-0 flex items-center justify-center text-white font-bold text-lg hover:opacity-80 transition-opacity focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        style={{ backgroundColor: staffMember.color }}
                        title="View Profile"
                    >
                        {getInitials(staffMember.name)}
                    </button>
                    <div className="flex-grow min-w-0">
                        <p className="text-xs font-bold text-indigo-600 dark:text-indigo-400 uppercase tracking-wider truncate mb-0.5">{propertyName}</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Welcome back,</p>
                        <h1 className="text-xl font-bold text-gray-800 dark:text-gray-200 leading-tight truncate">{staffMember.name}</h1>
                    </div>
                    <div className="flex-shrink-0 flex items-center gap-1">
                        <button
                            onClick={() => setActiveTab('help')}
                            className="p-2 rounded-full text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-slate-700 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                            title="Help"
                        >
                            <Icons.HelpCircle className="w-5 h-5" />
                        </button>
                        <NotificationBell unreadCount={unreadCount} onClick={() => setPortalView('notifications')} />
                        <ThemeToggle theme={theme} setTheme={setTheme} />
                        <button
                            onClick={onLogout}
                            className="p-2 rounded-full text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-slate-700 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                            title="Logout"
                        >
                            <Icons.LogOut className="w-5 h-5" />
                        </button>
                    </div>
                </div>
                {activeTab === 'schedule' && renderScheduleView()}
                {activeTab === 'shuttle' && renderShuttleScheduleView()}
                {activeTab === 'profile' && renderProfileView()}
                {activeTab === 'availability' && renderAvailabilityView()}
                {activeTab === 'hours' && (
                    <WorkingHoursTab 
                        attendanceRecords={attendanceRecords} 
                        personalSchedule={personalSchedule}
                        shifts={shifts} 
                        weekStartDay={weekStartDay} 
                        employeeId={staffMember.id} 
                    />
                )}
                {activeTab === 'help' && <HelpCenterView isMobile={true} />}
                {activeTab === 'memos' && <MemosView isAdmin={false} staffName={staffMember.name} />}
                {activeTab === 'taskManager' && <TaskManagerView staffName={staffMember.name} />}
                 {timeOffDeleteHandler.itemToDelete && (
                    <ConfirmationModal
                        title="Delete Time Off Request"
                        message={`Are you sure you want to delete your request for ${timeOffDeleteHandler.itemToDelete.startDate} to ${timeOffDeleteHandler.itemToDelete.endDate}?`}
                        confirmButtonText="Delete"
                        onConfirm={timeOffDeleteHandler.confirmDelete}
                        onCancel={timeOffDeleteHandler.cancelDelete}
                        isConfirming={timeOffDeleteHandler.isDeleting}
                    />
                )}
                {shuttleDeleteHandler.itemToDelete && (
                    <ConfirmationModal
                        title="Cancel Shuttle Reservation"
                        message={`Are you sure you want to cancel the reservation for ${shuttleDeleteHandler.itemToDelete.roomNumber} at ${shuttleDeleteHandler.itemToDelete.time}?`}
                        confirmButtonText="Cancel Reservation"
                        onConfirm={shuttleDeleteHandler.confirmDelete}
                        onCancel={shuttleDeleteHandler.cancelDelete}
                        isConfirming={shuttleDeleteHandler.isDeleting}
                    />
                )}
                 {selectedDateForDetails && (
                    <DailyShiftDetailsModal
                        date={selectedDateForDetails}
                        currentStaffMember={staffMember}
                        allStaff={allStaff}
                        schedule={schedule}
                        shifts={shifts}
                        departments={departments}
                        attendanceRecords={attendanceRecords}
                        attendanceRequests={attendanceRequests}
                        onUpdateScheduleEntry={onUpdateScheduleEntry}
                        onClose={() => setSelectedDateForDetails(null)}
                    />
                )}
            </div>

            <div className="fixed bottom-0 left-0 right-0 h-20 bg-white dark:bg-slate-800 border-t border-gray-200 dark:border-slate-700 shadow-[0_-2px_10px_rgba(0,0,0,0.05)]">
                 <div className={`max-w-md mx-auto h-full grid ${(tabs?.length || 0) === 6 ? 'grid-cols-6' : (tabs?.length || 0) === 5 ? 'grid-cols-5' : (tabs?.length || 0) === 4 ? 'grid-cols-4' : (tabs?.length || 0) === 3 ? 'grid-cols-3' : 'grid-cols-2'}`}>
                     {(tabs || []).map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id as any)}
                            className={`flex flex-col items-center justify-center gap-1 transition-colors duration-200 ${
                                activeTab === tab.id ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-500 dark:text-gray-400 hover:text-indigo-500 dark:hover:text-indigo-400'
                            }`}
                        >
                            {tab.icon}
                            <span className="text-xs font-medium">{tab.label}</span>
                        </button>
                     ))}
                </div>
            </div>
        </div>
    );
};

export default StaffPortal;